import React from "react";
import { useTranslation } from "react-i18next";
import { Form, Input, Select, Table, Button, Dropdown, Menu } from "antd";
import { DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import styles from "./ViewReward.module.scss";

export interface ClientsFiltersState {
  clientId?: string;
  name?: string;
  email?: string;
  idNumber?: string;
  mobile?: string;
}

export interface ViewRewardClientsTabProps {
  reward: any;
  canEdit: boolean;
  clientsFilters: ClientsFiltersState;
  setClientsFilters: React.Dispatch<React.SetStateAction<ClientsFiltersState>>;
  clientsPage: {
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  } | null;
  loadingClients: boolean;
  onClientsTableChange: (pagination: any) => void;
  onRemoveClientFromReward: (clientId: number) => void;
  onOpenAddClientsModal: () => void;
  levels: any[];
  onSearchLevels: (value: string) => void;
  getOptions: (items: any) => React.ReactNode;
}

const ViewRewardClientsTab: React.FC<ViewRewardClientsTabProps> = ({
  reward,
  canEdit,
  clientsFilters,
  setClientsFilters,
  clientsPage,
  loadingClients,
  onClientsTableChange,
  onRemoveClientFromReward,
  onOpenAddClientsModal,
  levels,
  onSearchLevels,
  getOptions,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-Level")}
          name="levelIds"
          initialValue={reward?.levels?.map((lev: any) => lev.id)}
        >
          <Select
            allowClear
            mode="multiple"
            showSearch
            placeholder={t("Common-SearchByName")}
            onSearch={onSearchLevels}
            filterOption={(input: string, option: any) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={!canEdit}
          >
            {getOptions(levels)}
          </Select>
        </Form.Item>
      </div>
      <div className={styles.sectionTitle}>{t("Common-Clients")}</div>
      <div className={styles.clientsSection}>
        <div className={styles.clientsFilters}>
          <Input
            placeholder={t("Common-ClientId")}
            value={clientsFilters.clientId || ""}
            onChange={(e) =>
              setClientsFilters((f) => ({
                ...f,
                clientId: e.target.value.trim() || undefined,
              }))
            }
            allowClear
            className={styles.clientFilterInput}
          />
          <Input
            placeholder={t("Common-Name")}
            value={clientsFilters.name || ""}
            onChange={(e) =>
              setClientsFilters((f) => ({ ...f, name: e.target.value || undefined }))
            }
            allowClear
            className={styles.clientFilterInput}
          />
          <Input
            placeholder={t("Common-Email")}
            value={clientsFilters.email || ""}
            onChange={(e) =>
              setClientsFilters((f) => ({ ...f, email: e.target.value || undefined }))
            }
            allowClear
            className={styles.clientFilterInput}
          />
          <Input
            placeholder={t("Common-IDNumber")}
            value={clientsFilters.idNumber || ""}
            onChange={(e) =>
              setClientsFilters((f) => ({ ...f, idNumber: e.target.value || undefined }))
            }
            allowClear
            className={styles.clientFilterInput}
          />
          <Input
            placeholder={t("Common-Mobile")}
            value={clientsFilters.mobile || ""}
            onChange={(e) =>
              setClientsFilters((f) => ({ ...f, mobile: e.target.value || undefined }))
            }
            allowClear
            className={styles.clientFilterInput}
          />
          {canEdit && (
            <Button
              type="primary"
              onClick={onOpenAddClientsModal}
              className={styles.addNewClientsBtn}
            >
              <span>{t("Common-AddNewClients")}</span>
            </Button>
          )}
        </div>
        <div className={styles.clientsTableWrapper}>
          <Table
            loading={loadingClients}
            dataSource={clientsPage?.content || []}
            rowKey="id"
            bordered
            pagination={{
              current: (clientsPage?.number ?? 0) + 1,
              pageSize: clientsPage?.size || 20,
              total: clientsPage?.totalElements ?? 0,
              showSizeChanger: true,
              showTotal: (total) => `${t("Common-Total")} ${total}`,
              pageSizeOptions: ["10", "20", "50"],
              locale: {
                items_per_page: t("Common-PaginationPerPage"),
              },
            }}
            onChange={onClientsTableChange}
            columns={[
              {
                title: t("Common-ClientId"),
                dataIndex: "id",
                key: "id",
                width: 80,
                align: "right",
              },
              {
                title: t("Common-IDNumber"),
                dataIndex: "idNumber",
                key: "idNumber",
                render: (val: string) => val || "—",
              },
              {
                title: t("Common-Name"),
                dataIndex: "name",
                key: "name",
                render: (_: any, r: any) =>
                  `${r.name || ""} ${r.lastName || ""}`.trim() || "—",
              },
              {
                title: t("Common-Email"),
                dataIndex: "email",
                key: "email",
                render: (val: string) => val || "—",
              },
              {
                title: t("Common-Mobile"),
                dataIndex: "mobile",
                key: "mobile",
                render: (val: string) => val || "—",
              },
              ...(canEdit
                ? [
                    {
                      title: t("Common-Actions"),
                      key: "actions",
                      width: 80,
                      align: "center" as const,
                      render: (_: any, record: any) => (
                        <Dropdown
                          overlay={
                            <Menu>
                              <Menu.Item
                                key="remove"
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => onRemoveClientFromReward(record.id)}
                              >
                                {t("Common-Remove")}
                              </Menu.Item>
                            </Menu>
                          }
                        >
                          <Button className={styles.actionButtonMore}>
                            <MoreOutlined className={styles.actionIconMore} />
                          </Button>
                        </Dropdown>
                      ),
                    },
                  ]
                : []),
            ]}
            size="middle"
          />
        </div>
      </div>
    </>
  );
};

export default ViewRewardClientsTab;
